import { useEffect, useState } from "react";

import { peaksFromBuffer } from "../domain/waveform";

/**
 * The shape of a take, read once it exists.
 *
 * Decoding a few minutes of Opus takes a moment and holds the whole recording
 * in memory, so it happens once per take and the result is thrown away with
 * it. A browser that cannot decode what it just recorded — or a jsdom that
 * cannot decode anything — leaves the lane empty rather than failing: the
 * waveform is how the take is read, not whether it exists.
 */
export function useAudioPeaks(blob: Blob | null): number[] {
    const [peaks, setPeaks] = useState<number[]>([]);

    useEffect(() => {
        if (!blob) {
            setPeaks([]);
            return;
        }

        let cancelled = false;
        const AudioContextCtor =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;
        if (!AudioContextCtor) return;

        const context = new AudioContextCtor();
        void blob
            .arrayBuffer()
            .then((bytes) => context.decodeAudioData(bytes))
            .then((buffer) => {
                if (!cancelled) setPeaks(peaksFromBuffer(buffer));
            })
            .catch(() => {
                if (!cancelled) setPeaks([]);
            })
            .finally(() => void context.close().catch(() => undefined));

        return () => {
            cancelled = true;
        };
    }, [blob]);

    return peaks;
}
